import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { Link as ScrollLink } from 'react-scroll';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import { getCardLabelData, getFileData } from 'Components/PickAndMix/PickAndMixHelper';
import BundleProductCardCover from 'Components/Bundle/Cards/BundleProductCardCover';
import SelectButton from 'Components/PickAndMix/SelectButton';
import DlcBadge from 'Components/Cards/DlcBadge';
import Drm from 'Components/Utility/Drm';
import { currencyCode } from 'Helpers/Geo/data';
import SpecialFormattedNumber from 'Components/Utility/Price/SpecialFormattedNumber';
import getBundleCardType from 'Helpers/Product/BundleCardType';
import useViewport from 'Hooks/useViewport';
import PickAndMixSteamDeckIcons from 'Components/PickAndMix/Cards/PickAndMixSteamDeckIcons';
import PickAndMixCriticScore from 'Components/PickAndMix/Cards/PickAndMixCriticScore';
import PickAndMixStars from 'Components/PickAndMix/Cards/PickAndMixStars';

import 'Components/PickAndMix/Cards/PickAndMixCard.scss';

const CardLabel = ({ labelData }) => {
  const { formatMessage } = useIntl();

  return (
    <div className={`card-label ${labelData.labelClass}`}>
      <div className="label-container">
        <div className="label-content">
          {formatMessage(labelData.message)}
        </div>
        <div className="label-slash" />
      </div>
      <div className="label-triangle" />
    </div>
  );
};

const OperatingSystems = ({ os }) => {
  if (isEmpty(os)) return false;

  return (
    <div className="PickAndMixCard__platforms">
      <React.Fragment>
        {os.includes('windows') &&
          <FontAwesomeIcon icon={fabFaWindows} size="lg" />
        }
        {os.includes('mac') &&
          <FontAwesomeIcon icon={fabFaApple} size="lg" />
        }
        {os.includes('linux') &&
          <FontAwesomeIcon icon={fabFaLinux} size="lg" />
        }
      </React.Fragment>
    </div>
  );
};

const PickAndMixCard = ({
  hit,
  cartSlugs,
  inLibrary,
  inSteamLibrary,
  lazy,
  sizesOverride,
  switchCarouselGame,
  isPartOfPreselected,
  preSelectedType,
  siteTheme,
  forceLightTheme,
}) => {
  const {
    cover,
    slug,
    fullPrice,
    drm,
    type,
    downloads,
    operating_systems: OS,
    low_stock: lowStock,
    display_type: displayType,
    cover_type: coverType,
    total_game_index: totalGameIndex,
    soldOut,
    product_id: productId,
    steam_deck_support: steamDeckSupport,
    metacritic_score: metaCriticScore,
    opencritic_score: openCriticScore,
    rating_score: ratingScore,
    name,
  } = hit;
  const selected = cartSlugs.includes(slug);
  const { isXs, isAboveMd } = useViewport();
  const { fileCount } = getFileData(downloads);
  const cardLabelData = getCardLabelData(lowStock, soldOut, inLibrary, inSteamLibrary);
  const cardType = getBundleCardType(type, displayType, coverType, true);
  const showTopRow = !isEmpty(drm) || (type === 'dlc' || displayType === 'dlc') || !isEmpty(OS);
  const showMiddleRow = ['steamDeckVerified', 'criticallyAcclaimed', 'fanFavourites'].includes(preSelectedType);

  const handleGameClick = () => {
    switchCarouselGame(totalGameIndex);
  };

  return (
    <article
      className={classNames(
        cardType,
        { 'card-selected': selected },
        { 'sold-out': soldOut },
        { 'faux-block-link': !soldOut },
        { 'PickAndMixCard--light': forceLightTheme },
      )}
    >
      {type === 'comic' && fileCount > 1 &&
        <div className="PickAndMixCard__issueLabel ">
          {fileCount}
        </div>
      }

      {cardLabelData.shouldShowLabel &&
        <CardLabel labelData={cardLabelData} />
      }


      <div className="cover-container">
        <BundleProductCardCover
          lazy={lazy}
          cover={cover}
          name={name}
          type={type}
          displayType={displayType}
          coverType={coverType}
          sizesOverride={sizesOverride}
        />
        <div className="overlay-container">
          <div className="card-overlay">
            <div>
              <h2 className="card-product-name">{name}</h2>
              <ScrollLink
                spy
                smooth="true"
                offset={-120}
                to="carousel"
                duration={500}
                className="faux-block-link__overlay-link"
                onClick={handleGameClick}
              />
            </div>
          </div>
        </div>
      </div>


      <div className="PickAndMixCard__cardContent">
        {showTopRow &&
          <div className="PickAndMixCard__topRowIcons">
            <div className="PickAndMixCard__leftIcons">
              <div className="drm-dlc-container">
                {!isEmpty(drm) &&
                  <Drm
                    drmType={drm[0]}
                    colorOverride={forceLightTheme ? 'dark' : ''}
                  />
                }

                {(type === 'dlc' || displayType === 'dlc') &&
                  <DlcBadge />
                }
              </div>
              <OperatingSystems os={OS} />
            </div>
          </div>
        }


        {showMiddleRow &&
          <div className="PickAndMixCard__middleRowIcons">
            {preSelectedType === 'steamDeckVerified' &&
              <PickAndMixSteamDeckIcons
                steamDeckSupport={steamDeckSupport}
                siteTheme={forceLightTheme ? 'light' : siteTheme}
              />
            }

            {preSelectedType === 'criticallyAcclaimed' &&
              <PickAndMixCriticScore
                opencriticScore={openCriticScore}
                metaCriticScore={metaCriticScore}
              />
            }

            {preSelectedType === 'fanFavourites' &&
              <PickAndMixStars ratingScore={ratingScore} />
            }
          </div>
        }

        <div className="PickAndMixCard__bottomRowIcons">
          {!isPartOfPreselected &&
            <div className="PickAndMixCard__productDetails">
              <ScrollLink
                spy
                smooth="true"
                offset={-120}
                to="carousel"
                duration={500}
                className="faux-block-link__overlay-link"
                onClick={handleGameClick}
              >

                {!isXs &&
                  <FormattedMessage id="PickAndMixCard.productdetails" defaultMessage="Product Details" />
                }

                {isXs &&
                  <FormattedMessage id="PickAndMixCard.details" defaultMessage="Details" />
                }

              </ScrollLink>
            </div>
          }

          <div className="PickAndMixCard__price">
            <span className="PickAndMixCard__price__rrp mr-1">RRP</span>
            <SpecialFormattedNumber
              value={fullPrice[`${currencyCode}`]}
              style="currency"
            />
          </div>

        </div>
        {isAboveMd && !isPartOfPreselected &&
          <div className="PickAndMixCard__addToBundle">
            <SelectButton
              desktopButton
              soldOut={soldOut}
              id={productId}
              cover={cover}
              selected={selected}
              slug={slug}
            />
          </div>
        }
      </div>

      {!isPartOfPreselected &&
        <div className="mobile-select-button-container d-lg-none">
          <SelectButton
            soldOut={soldOut}
            id={productId}
            cover={cover}
            selected={selected}
            slug={slug}
          />
        </div>
      }

    </article>
  );
};

const mapStateToProps = ({
  cart: { allSlugs: cartSlugs },
  userLibrary,
  steamLibrary,
  siteTheme,
}, { hit: { name } }) => ({
  cartSlugs,
  inLibrary: userLibrary.includes(name),
  inSteamLibrary: steamLibrary.includes(name),
  siteTheme,
});


PickAndMixCard.propTypes = {
  hit: PropTypes.shape({}),
  isPartOfPreselected: PropTypes.bool,
  switchCarouselGame: PropTypes.func.isRequired,
  forceLightTheme: PropTypes.bool,
};

PickAndMixCard.defaultProps = {
  hit: null,
  siteTheme: 'dark',
  isPartOfPreselected: false,
  forceLightTheme: false,
};

export default connect(mapStateToProps)(PickAndMixCard);
